<template>
  <v-dialog
    style="padding: 0 !important"
    v-model="dialog"
    max-width="700px"
    persistent
    @click:outside="handleCloseCurrentModal"
  >
    <v-card class="pa-0">
      <!-- Header -->
      <div class="pa-4 d-flex justify-space-between align-center header-modal">
        <h3 class="mb-0">Product Reviews Options</h3>
        <v-btn icon @click="handleCloseCurrentModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Content -->
      <div class="content-modal">
        <v-card>
          <v-tabs v-model="defaultTab" background-color="primary" dark style="border-radius: 0 !important">
            <v-tab>Random</v-tab>
            <v-tab>Import from CSV</v-tab>
          </v-tabs>

          <v-tabs-items v-model="defaultTab">
            <!-- Tab Default -->
            <v-tab-item>
              <div class="pa-4">
                <div>
                  <p>Enter the number of reviews you want to generate</p>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="randomReviewsCount"
                        type="number"
                        min="1"
                        :max="maxRandomReviewsCount"
                        label="Random Reviews Count"
                        :rules="[
                          v =>
                            (v >= 1 && v <= maxRandomReviewsCount) ||
                            `Random reviews count must be between 1 and ${maxRandomReviewsCount}`,
                        ]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select v-model="selectedGender" :items="genderOptions" label="Gender" />
                    </v-col>
                  </v-row>
                </div>
                <div v-if="showAdditionalInfoFields">
                  <p class="mb-2">Add additional information as needed</p>
                  <v-row>
                    <v-col cols="12" :md="additionalInfoColMd">
                      <v-text-field
                        v-model.number="randomFieldsMore.averageRating"
                        label="Average Rating ⭐"
                        type="number"
                        min="0.1"
                        :rules="[v => (v >= 0.1 && v <= 5) || 'Average rating must be between 0.1 and 5.0']"
                      />
                    </v-col>
                    <v-col cols="12" :md="additionalInfoColMd">
                      <v-text-field
                        v-model.number="randomFieldsMore.totalReviews"
                        label="Total Reviews"
                        type="number"
                        min="1"
                        inputmode="numeric"
                        :rules="[
                          v =>
                            (Number.isInteger(v) && v >= 1) ||
                            'Total reviews must be greater than 1 & must be an integer only',
                        ]"
                      />
                    </v-col>
                    <v-col v-if="showRecommendCountField" cols="12" :md="additionalInfoColMd">
                      <v-text-field
                        v-model.number="randomFieldsMore.recommendCount"
                        label="Recommend Count"
                        type="number"
                        min="0"
                        inputmode="numeric"
                        :rules="[
                          v =>
                            (Number.isInteger(v) && v >= 1) ||
                            'Recommend count must be greater than 1 & must be an integer only',
                        ]"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-tab-item>

            <!-- Tab Import CSV -->
            <v-tab-item>
              <div class="pa-4">
                <h4 class="mb-4">Upload CSV File</h4>
                <v-form ref="formCsv" v-model="formValid" lazy-validation>
                  <!-- Upload zone -->
                  <div
                    class="upload-zone"
                    @dragover.prevent
                    @dragenter.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleDrop"
                    :class="{ 'upload-zone--active': isDragging }"
                  >
                    <v-icon large color="primary">mdi-file-upload</v-icon>
                    <p class="mb-2">Drag & drop your CSV file here</p>
                    <v-btn small outlined color="primary" @click="$refs.csvInput.click()"> Browse Files </v-btn>
                    <input type="file" ref="csvInput" accept=".csv" style="display: none" @change="handleFileSelect" />
                  </div>

                  <!-- Loading + Progress -->
                  <!-- <div class="mt-6 text-center" v-if="csvLoading">
                    <p>Importing CSV... {{ csvProgress }}%</p>
                    <v-progress-linear :value="csvProgress" max="100" color="primary" height="8"
                      rounded></v-progress-linear>
                  </div> -->

                  <!-- File info -->
                  <div v-if="csvFile && !csvLoading" class="mt-4 d-flex flex-column flex-md-row align-center">
                    <v-chip color="primary" text-color="white">
                      {{ csvFile.name }}
                      <v-icon right small @click="handleRemoveCsvFile">mdi-close</v-icon>
                    </v-chip>
                    <div class="ml-0 ml-md-4 mt-2 mt-md-0 primary--text">
                      <span>Finished importing:</span> {{ importedReviews.length }} reviews
                      <span v-if="duplicateCount > 0">
                        (<span>{{ importedReviews.length - duplicateCount }} valid</span>
                        <span>, {{ duplicateCount }} {{ duplicateCount > 1 ? 'duplicates' : 'duplicate' }}</span
                        >)
                      </span>
                    </div>
                  </div>

                  <!-- Additional information -->
                  <div v-if="csvFile && !csvLoading && showAdditionalInfoFields" class="mt-4">
                    <p class="mb-2">Add additional information as needed</p>
                    <v-row>
                      <v-col cols="12" :md="additionalInfoColMd">
                        <v-text-field
                          v-model.number="importedFieldsMore.averageRating"
                          label="Average Rating ⭐"
                          type="number"
                          min="0.1"
                          :rules="[v => (v >= 0.1 && v <= 5) || 'Average rating must be between 0.1 and 5.0']"
                        />
                      </v-col>
                      <v-col cols="12" :md="additionalInfoColMd">
                        <v-text-field
                          v-model.number="importedFieldsMore.totalReviews"
                          label="Total Reviews"
                          type="number"
                          min="1"
                          inputmode="numeric"
                          :rules="[
                            v =>
                              (Number.isInteger(v) && v >= 1) ||
                              'Total reviews must be greater than 1 & must be an integer only',
                          ]"
                        />
                      </v-col>
                      <v-col v-if="showRecommendCountField" cols="12" :md="additionalInfoColMd">
                        <v-text-field
                          v-model.number="importedFieldsMore.recommendCount"
                          label="Recommend Count"
                          type="number"
                          min="0"
                          inputmode="numeric"
                          :rules="[
                            v =>
                              (Number.isInteger(v) && v >= 1) ||
                              'Recommend count must be greater than 1 & must be an integer only',
                          ]"
                        />
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Alerts -->
                  <v-alert v-if="!csvFile && !csvLoading" type="info" class="mt-4" dense border="left" colored-border>
                    Please upload a CSV file containing your product reviews data.
                  </v-alert>

                  <div class="mt-4">
                    Download a
                    <a
                      href="https://static.cdnstash.com/assets/csv/product-reviews-template.csv"
                      class="primary--text"
                      download
                      >sample CSV template</a
                    >
                    to see an example of the format required.
                  </div>

                  <v-alert v-if="csvError" type="error" class="mt-2" dense border="left" colored-border>
                    <div v-html="csvError" style="color: red"></div>
                  </v-alert>
                </v-form>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </div>

      <!-- Footer -->
      <div class="footer-modal">
        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <div class="d-flex justify-end py-2">
            <v-btn @click="handleCloseCurrentModal">Close</v-btn>
            <v-btn
              @click="handleSave"
              :disabled="defaultTab === 1 && !csvFile && !importedReviews.length"
              color="primary"
              class="ml-4"
            >
              Save
            </v-btn>
          </div>
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import productReviewsJson from '@/assets/json/product-reviews.json';
import { sectionType } from '@/views/website/products/components/description/common/types';
export default {
  name: 'ProductReviewsOptions',
  props: {
    show: { type: Boolean, default: false, required: true },
    template: { type: Object, default: null },
    onSelect: { type: Function, default: () => {} },
    productReviewType: { type: String, default: '' },
  },
  data() {
    return {
      dialog: this.show,
      defaultTab: 0,
      templateFinal: this.template,
      csvFile: null,
      csvError: '',
      formValid: false,
      isDragging: false,
      importedReviews: [],
      randomReviewsCount: this.randomReviewsCountComputed,
      maxRandomReviewsCount: 10,
      selectedGender: 'Unisex',
      genderOptions: [
        { text: 'Unisex', value: 'Unisex' },
        { text: 'Male', value: 'Male' },
        { text: 'Female', value: 'Female' },
      ],
      csvLoading: false,
      csvProgress: 0,
      duplicateCount: 0,
      randomFieldsMore: {
        averageRating: 5,
        totalReviews: this.randomReviewsCountComputed,
        recommendCount: 8,
      },
      importedFieldsMore: {
        averageRating: 5,
        totalReviews: this.randomReviewsCountComputed,
        recommendCount: 8,
      },
    };
  },
  computed: {
    productReviewTypeNormalized() {
      return (this.productReviewType || '').trim();
    },
    isTypeProductReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews;
    },
    isTypeProductReviews02() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews02;
    },
    isTypeProductReviews03() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews03;
    },
    isTypeProductReviews04() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews04;
    },
    isTypeProductReviews05() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews05;
    },
    isTypeProductReviews06() {
      return this.productReviewTypeNormalized === sectionType.sectionProductReviews06;
    },
    isTypeGatorzReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionGatorzReviews;
    },
    isTypeFastvaluedealReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionFastvaluedealReviews;
    },
    isTypeSaintbrandReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionSaintbrandReviews;
    },
    isTypeLosaprilyReviews() {
      return this.productReviewTypeNormalized === sectionType.sectionLosaprilyReviews;
    },
    showAdditionalInfoFields() {
      return this.isTypeProductReviews || this.isTypeProductReviews02 || this.isTypeProductReviews05;
    },
    showRecommendCountField() {
      return this.isTypeProductReviews;
    },
    additionalInfoColMd() {
      return this.isTypeProductReviews ? 4 : 6;
    },
    randomReviewsCountComputed() {
      return this.isTypeFastvaluedealReviews || this.isTypeLosaprilyReviews ? 15 : 10;
    },
  },
  methods: {
    handleCloseCurrentModal() {
      this.dialog = false;
      this.$emit('close');
      this.resetModalState();
    },
    resetModalState() {
      this.handleRemoveCsvFile();
      this.defaultTab = 0;
      this.csvLoading = false;
      this.csvProgress = 0;
      this.csvError = '';
      this.randomFieldsMore = {
        averageRating: 5,
        totalReviews: this.randomReviewsCountComputed,
        recommendCount: 8,
      };
      this.importedFieldsMore = {
        averageRating: 5,
        totalReviews: this.randomReviewsCountComputed,
        recommendCount: 8,
      };
      this.randomReviewsCount = this.randomReviewsCountComputed;
      this.selectedGender = 'Unisex';
      this.importedReviews = [];
      this.duplicateCount = 0;
    },
    async handleSave() {
      // Validate templateFinal exists
      if (!this.templateFinal || !this.templateFinal.html) {
        console.error('Template is not available');
        return;
      }

      if (this.defaultTab === 0) {
        // random reviews - random from JSON array with gender filter
        let reviewsRandom = [];
        let offsetDays = 0;
        let usedIndices = new Set();

        // Filter reviews by gender if not Unisex
        let filteredReviews = productReviewsJson;
        if (this.selectedGender !== 'Unisex') {
          filteredReviews = productReviewsJson.filter(review => review.gender === this.selectedGender);
        }

        // Update max count based on filtered results
        const availableCount = filteredReviews.length;
        const actualCount = Math.min(this.randomReviewsCount, availableCount);

        for (let i = 0; i < actualCount; i++) {
          let randomIndex;
          do {
            randomIndex = Math.floor(Math.random() * filteredReviews.length);
          } while (usedIndices.has(randomIndex));

          usedIndices.add(randomIndex);
          const review = filteredReviews[randomIndex];

          // Push offset date random (1-7)
          offsetDays += Math.floor(Math.random() * 7) + 1;

          const pastDate = new Date();
          pastDate.setDate(pastDate.getDate() - offsetDays);

          // Random hour, minute, second
          pastDate.setHours(Math.floor(Math.random() * 24)); // 0-23h
          pastDate.setMinutes(Math.floor(Math.random() * 60)); // 0-59p
          pastDate.setSeconds(Math.floor(Math.random() * 60)); // 0-59s

          reviewsRandom.unshift({
            fullName: review.fullName || '',
            avatar: review.avatar || '',
            title: '',
            content: '',
            star: 5,
            date: this.formatDateToDisplay(pastDate),
            dateValue: this.formatDateToISO(pastDate),
            images: [],
          });
        }

        const html = updateHtmlFromData.sectionProductReviews(
          this.templateFinal.html,
          {
            'imported-reviews': reviewsRandom,
            'add-more-info-when-create': this.randomFieldsMore,
          },
          this.templateFinal.type,
        );
        this.templateFinal.html = html;
      } else if (this.defaultTab === 1) {
        // import from csv - remove duplicates based on fullName + avatar
        if (!this.importedReviews.length) {
          this.csvError = 'Please upload a valid CSV file before saving.';
          return;
        }

        // Remove duplicates based on fullName + avatar combination
        const uniqueReviews = [];
        const seenCombinations = new Set();

        for (const review of this.importedReviews) {
          const combination = `${review.fullName}-${review.avatar}`;
          if (!seenCombinations.has(combination)) {
            seenCombinations.add(combination);
            uniqueReviews.push(review);
          }
        }

        // Update the imported reviews with unique ones
        this.importedReviews = uniqueReviews;

        const html = updateHtmlFromData.sectionProductReviews(
          this.templateFinal.html,
          {
            'imported-reviews': this.importedReviews,
            'add-more-info-when-create': this.importedFieldsMore,
          },
          this.templateFinal.type,
        );

        this.templateFinal.html = html;
      }
      this.onSelect(this.templateFinal);
      this.handleCloseCurrentModal();
    },
    handleFileSelect(e) {
      const file = e.target.files[0];
      this.validateAndImportFile(file);
      e.target.value = null;
    },
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      this.validateAndImportFile(file);
    },
    validateAndImportFile(file) {
      if (!file) return;
      if (!file.name.endsWith('.csv')) {
        this.csvError = 'File must be a .csv';
        this.csvFile = null;
        return;
      }
      this.csvError = '';
      this.csvFile = file;
      this.importCsv(file);
    },
    handleRemoveCsvFile() {
      this.csvFile = null;
      this.csvError = '';
      this.importedReviews = [];
      this.duplicateCount = 0;
      if (this.$refs.csvInput) {
        this.$refs.csvInput.value = null;
      }
    },
    async importCsv(file) {
      return new Promise(resolve => {
        const reader = new FileReader();
        this.csvLoading = true;
        this.csvProgress = 0;
        this.csvError = '';
        this.importedReviews = [];

        reader.onload = e => {
          try {
            let text = e.target.result.replace(/^\uFEFF/, '');
            const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
            if (lines.length < 2) {
              this.csvError = 'CSV file must have a header and at least one row.';
              this.csvLoading = false;
              resolve(false);
              return;
            }

            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            const requiredHeaders = ['name', 'avatar', 'rating', 'title', 'content', 'date'];
            const missing = requiredHeaders.filter(h => !headers.includes(h));
            if (missing.length > 0) {
              this.csvError = `Missing required columns: <span style="text-transform: capitalize;">${missing.join(
                ', ',
              )}</span>`;
              this.csvLoading = false;
              resolve(false);
              return;
            }

            const imageCols = {};
            headers.forEach((h, i) => {
              const match = h.match(/^image(\d+)$/i);
              if (match) imageCols[parseInt(match[1], 10)] = i;
            });

            const rows = lines.slice(1);
            const chunkSize = 200;
            const processChunk = async start => {
              const end = Math.min(start + chunkSize, rows.length);

              for (let i = start; i < end; i++) {
                const cols = this.parseCsvLine(rows[i]);
                const row = {};
                headers.forEach((h, idx) => (row[h] = cols[idx] ? cols[idx].trim() : ''));

                let rating = parseInt(row.rating, 10);
                if (isNaN(rating) || rating < 1 || rating > 5) rating = 5;
                let avatar = this.isValidUrl(row.avatar) ? row.avatar : '';
                const images = Object.keys(imageCols)
                  .sort((a, b) => a - b)
                  .map(idx => {
                    const url = cols[imageCols[idx]];
                    return url && this.isValidUrl(url) ? { src: url, alt: '' } : null;
                  })
                  .filter(Boolean);

                let finalDate = new Date(row.date);
                if (isNaN(finalDate.getTime())) finalDate = new Date();

                this.importedReviews.push({
                  fullName: row.name || '',
                  avatar,
                  star: rating,
                  title: row.title || '',
                  content: row.content || '',
                  images,
                  dateValue: this.formatDateToISO(finalDate),
                  date: this.formatDateToDisplay(finalDate),
                });
              }

              // update progress
              this.csvProgress = Math.round((end / rows.length) * 100);

              // wait for Vue update DOM
              await this.$nextTick();
              await new Promise(r => setTimeout(r, 0));

              if (end < rows.length) {
                await processChunk(end);
              } else {
                this.csvLoading = false;
                resolve(true);
              }
            };

            processChunk(0);

            // Calculate duplicates after import
            this.calculateDuplicateCount();
            this.importedFieldsMore.totalReviews = this.importedReviews.length - this.duplicateCount;
          } catch (err) {
            this.csvError = 'CSV parse error: ' + err.message;
            this.csvLoading = false;
            resolve(false);
          }
        };

        reader.onerror = () => {
          this.csvError = 'Failed to read file.';
          this.csvLoading = false;
          resolve(false);
        };
        reader.readAsText(file);
      });
    },
    parseCsvLine(line) {
      const result = [];
      let current = '';
      let insideQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current);

      return result;
    },

    isValidUrl(url) {
      try {
        const u = new URL(url);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch (_) {
        return false;
      }
    },
    formatDateToISO(date) {
      if (!date) return new Date().toISOString().substr(0, 10);
      const parsedDate = new Date(date);
      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().substr(0, 10);
      } else {
        // Fallback to current date if parsing fails
        return new Date().toISOString().substr(0, 10);
      }
    },
    formatDateToDisplay(date) {
      if (!date) date = new Date();

      // If it's already a Date object, use it directly
      let parsedDate;
      if (date instanceof Date) {
        parsedDate = date;
      } else {
        parsedDate = new Date(date);
      }

      // Check if the date is valid
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
      } else {
        // Fallback to current date if parsing fails
        return new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        });
      }
    },
    calculateDuplicateCount() {
      const seenCombinations = new Set();
      let duplicateCount = 0;

      for (const review of this.importedReviews) {
        const combination = `${review.fullName}-${review.avatar}`;
        if (seenCombinations.has(combination)) {
          duplicateCount++;
        } else {
          seenCombinations.add(combination);
        }
      }

      this.duplicateCount = duplicateCount;
    },
    updateMaxRandomReviewsCount() {
      // Filter reviews by gender if not Unisex
      let filteredReviews = productReviewsJson;
      if (this.selectedGender !== 'Unisex') {
        filteredReviews = productReviewsJson.filter(review => review.gender === this.selectedGender);
      }
      this.maxRandomReviewsCount = filteredReviews.length;
    },
  },
  watch: {
    show(newVal) {
      this.dialog = newVal;
      if (newVal) {
        // Set template first, then reset state
        this.templateFinal = this.template;
        // Reset state when modal opens
        this.resetModalState();
        this.updateMaxRandomReviewsCount();
      }
    },
    selectedGender() {
      this.updateMaxRandomReviewsCount();
    },
    template: {
      handler(newVal) {
        if (newVal && this.show) {
          this.templateFinal = newVal;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  position: sticky;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fff;
  padding: 0px;
}

.upload-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s;
}

.upload-zone--active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}
</style>
